import moment from "~/services/moment";
import difference from "lodash/difference";
import findKey from "lodash/findKey";
import forEach from "lodash/forEach";
import get from "lodash/get";
import has from "lodash/has";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isPlainObject from "lodash/isPlainObject";
import isString from "lodash/isString";
import set from "lodash/set";
import { snakeCase } from "~/utils/helpers/string";
import { parse } from "querystring";
import { AdminEntity, AdminRole } from "~/types/models/admin";
import { HEADSHOT_STYLES, HeadshotStyleProps, ROLE_ADMIN } from "~/constants";

export function convertNumberToWordsEN(value: number): string {
  value = Math.floor(value);
  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

  const numString = value.toString();

  if (value < 0) throw new Error("Negative numbers are not supported.");

  if (value === 0) return "zero";

  // the case of 1 - 20
  if (value < 20) {
    return ones[value];
  }

  if (numString.length === 2) {
    return `${tens[Number(numString[0])]} ${ones[Number(numString[1])]}`;
  }

  // 100 and more
  if (numString.length === 3) {
    if (numString[1] === "0" && numString[2] === "0") return `${ones[Number(numString[0])]} hundred`;
    return `${ones[Number(numString[0])]} hundred and ${convertNumberToWordsEN(+(numString[1] + numString[2]))}`;
  }

  if (numString.length === 4) {
    const end = +(numString[1] + numString[2] + numString[3]);
    if (end === 0) return `${ones[Number(numString[0])]} thousand`;
    if (end < 100) return `${ones[Number(numString[0])]} thousand and ${convertNumberToWordsEN(end)}`;
    return `${ones[Number(numString[0])]} thousand ${convertNumberToWordsEN(end)}`;
  }
  return "";
}

export function snakeCaseObj(obj: Record<string, any>, removeWhitespace = true) {
  if (!isPlainObject(obj)) {
    return obj;
  }
  const res: Record<string, any> = {};
  Object.keys(obj).forEach(property => {
    let value = obj[property];
    if (isArray(value)) {
      value = value.map(item => snakeCaseObj(item));
    } else if (isPlainObject(value)) {
      value = snakeCaseObj(value);
    }
    if (removeWhitespace) {
      if (isString(value)) {
        value = value.replace(/\\t$/, "").trim();
      }
    }
    res[snakeCase(property)] = value;
  });

  return res;
}

export function parseFormDataToObj(formData: FormData) {
  const object: Record<string, any> = {};
  const formEntries = formData.entries();
  Object.keys(formEntries).forEach(key => {
    // @ts-ignore
    const value = formEntries[key];
    if (!(value instanceof File)) {
      object[key] = value;
    }
  });
  return object;
}

export function serializeQueryString(obj: Record<string, any>, { encode = true, snakeKey = false } = {}) {
  if (!obj) {
    return "";
  }
  return `?${Object.keys(obj)
    .reduce((a, k) => {
      if (Array.isArray(obj[k]) && obj[k].length > 0) {
        obj[k].forEach((val: any) => {
          const key = snakeKey ? snakeCase(k) : k;
          let value = val;
          if (isString(value)) {
            value = value.replace(/\\t$/, "").trim();
          }
          value = encode ? encodeURIComponent(value) : value;

          // @ts-ignore
          a.push(`${key}[]=${value}`);
        });
      } else if (obj[k] !== null && obj[k] !== undefined) {
        const key = snakeKey ? snakeCase(k) : k;
        let value = obj[k];
        if (isString(value)) {
          value = value.replace(/\\t$/, "").trim();
        }
        value = encode ? encodeURIComponent(value) : value;
        // @ts-ignore
        a.push(`${key}=${value}`);
      }
      return a;
    }, [])
    .join("&")}`;
}

export function findValuePath(obj: object, needle = "default"): string[] {
  const matchKey = findKey(obj, v => v === needle);
  if (matchKey) {
    return [matchKey];
  }
  const res: string[] = [];
  forEach(obj, (v, k) => {
    if (typeof v === "object") {
      const valuePath = findValuePath(v, needle);
      if (valuePath.length) {
        res.push(...(isArray(valuePath) ? valuePath : [valuePath]), k);
      }
    }
  });
  return res;
}

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function ensureSafeChaining(object: Object, path: string | string[], defaultValue?: any) {
  // Ensure the path is existed first
  if (!has(object, path)) {
    set(object, path, undefined);
  }
  // Set default value if you want to. (defaultValue !== undefined)
  if (defaultValue !== undefined && get(object, path) === undefined) {
    set(object, path, defaultValue);
  }
}

export function firstObjValue(obj: Record<any, any>) {
  // eslint-disable-next-line no-restricted-syntax
  for (const k in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(k)) {
      return obj[k];
    }
  }
  return undefined;
}

export function getObjNthItem(obj: Record<any, any>, nth: number) {
  if (!obj) {
    return undefined;
  }
  const index = nth - 1;
  const objKeys = Object.keys(obj);
  const targetKey = objKeys && objKeys[index];
  return obj[targetKey];
}

export const move = (array: any[], fromIndex: number, toIndex: number) => {
  const cloneArray = [...array];
  cloneArray.splice(toIndex, 0, cloneArray.splice(fromIndex, 1)[0]);
  return cloneArray;
};

export const getPageQuery = () => parse(window.location.href.split("?")[1]);

export const checkPermissions = (authorities: AdminRole[] = [], permissions: string[] = []) => {
  if (isEmpty(permissions)) {
    return true;
  }
  if (authorities.some(a => a.name === ROLE_ADMIN)) {
    return true;
  }
  const rolesCanNotAccess = difference(
    authorities.map(r => r.name),
    permissions
  );
  return rolesCanNotAccess.length < authorities.length;
};

export const checkSuperAdmin = (user?: AdminEntity) => {
  if (!user) return false;
  return checkPermissions(user?.roles || [], [ROLE_ADMIN]);
};

export const randomColor = () => Math.floor(Math.random() * 16777215).toString(16);

export const isSubstringArrayInString = (substringArray: string[], string: string) =>
  substringArray.some(substring => string.indexOf(substring) > -1);

export function fetchBinaryContent(url: string) {
  return new Promise((resolve, reject) => {
    // getBinaryContent(url, (err: any, data: any) => {
    //   if (err) {
    //     reject(err);
    //   } else {
    //     resolve(data);
    //   }
    // });
    fetch(url, {
      mode: "cors",
      cache: "no-cache",
    })
      .then(res => res.blob())
      .then(blob => resolve(blob))
      .catch(err => reject(err));
  });
}

const JOIN_STRING = "a4f3w3t442";

export function getStyleIdFromBgAndClothing(bg: string, clothing: string) {
  return `${bg}${JOIN_STRING}${clothing}`;
}

export function getBgAndClothingFromStyleId(styleId: string) {
  return styleId.split(JOIN_STRING);
}

export function getHeadshotStyleFromStyleId(styleId: string, isSelected: boolean = false): HeadshotStyleProps {
  const style = HEADSHOT_STYLES.find(s => s.styleId === styleId);
  if (!isEmpty(style)) {
    return { ...style, isSelected };
  }
  const [bg, clothing] = getBgAndClothingFromStyleId(styleId);
  return {
    styleId,
    // @ts-ignore
    bg: {
      title: bg,
      value: bg,
    },
    // @ts-ignore
    clothing: {
      label: clothing,
      value: clothing,
    },
    isSelected,
  };
}

export function generateGreetings() {
  const currentHour = Number(moment().format("HH"));

  if (currentHour >= 3 && currentHour < 12) {
    return "Good Morning";
  }
  if (currentHour >= 12 && currentHour < 15) {
    return "Good Afternoon";
  }
  if (currentHour >= 15 && currentHour < 20) {
    return "Good Evening";
  }
  if (currentHour >= 20 || currentHour < 3) {
    return "Good Night";
  }
  return "Hello";
}

export function numberToOrdinalWord(n: number): string {
  const ones = ["", "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth"];
  const teens = [
    "tenth",
    "eleventh",
    "twelfth",
    "thirteenth",
    "fourteenth",
    "fifteenth",
    "sixteenth",
    "seventeenth",
    "eighteenth",
    "nineteenth",
  ];
  const tens = [
    "",
    "",
    "twentieth",
    "thirtieth",
    "fortieth",
    "fiftieth",
    "sixtieth",
    "seventieth",
    "eightieth",
    "ninetieth",
  ];
  const tensPrefix = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

  if (n < 10) return ones[n];
  if (n < 20) return teens[n - 10];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const o = n % 10;
    return o === 0 ? tens[t] : `${tensPrefix[t]}-${ones[o]}`;
  }

  if (n < 1000) {
    const h = Math.floor(n / 100);
    const r = n % 100;
    if (r === 0) return `${ones[h].replace("first", "one")} hundredth`;
    return `${ones[h].replace("first", "one")} hundred ${numberToOrdinalWord(r)}`;
  }

  if (n < 10000) {
    const th = Math.floor(n / 1000);
    const r = n % 1000;
    if (r === 0) return `${ones[th].replace("first", "one")} thousandth`;
    return `${ones[th].replace("first", "one")} thousand ${numberToOrdinalWord(r)}`;
  }

  return "Number too large";
}
